import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Download, User, Phone, Mail, MapPin, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DynamicIsland from '../components/DynamicIsland';
import Footer from '../components/Footer';
import LeadFormPopup from '../components/LeadFormPopup';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { provinces } from '../mockData';
import { getLeadTrackingFields } from '../utils/leadTracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ClausolePage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', province: '', website: '' });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const canSubmit = form.name && form.phone && form.email && form.province && acceptTerms && acceptPrivacy;

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const triggerDownload = (url) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Clausole-Evoluzione-Tetto.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);

    try {
      const resp = await axios.post(`${BACKEND_URL}/api/clausole/request-download`, {
        name: form.name,
        phone: form.phone,
        email: form.email,
        province: form.province,
        acceptTerms,
        acceptPrivacy,
        canale: getLeadTrackingFields().canale,
        website: form.website,
      });

      const signedUrl = resp.data.downloadUrl;
      setDownloadUrl(signedUrl);

      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          send_to: 'AW-17960264240/HNy_CMqrr_obELDEj_RC',
          value: 1.0,
          currency: 'EUR',
        });
      }
      if (typeof window.fbq === 'function') window.fbq('track', 'Lead');

      toast.success('Link pronto! Ti abbiamo inviato anche una copia via email.');
      setSubmitted(true);
      triggerDownload(signedUrl);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'invio. Riprova.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <DynamicIsland onOpenForm={() => setIsFormOpen(true)} />

      {/* Hero */}
      <section className="pt-32 pb-14 bg-slate-50 border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3 py-1.5 mb-5">
              <FileText className="w-3.5 h-3.5 text-[#FF5722]" />
              <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                Documento riservato
              </span>
            </div>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4"
              style={{ fontFamily: 'Outfit' }}
              data-testid="clausole-title"
            >
              Clausole Contrattuali
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Scarica il documento ufficiale delle condizioni contrattuali applicabili ai lavori
              eseguiti dal marchio <strong className="text-slate-900">Evoluzione Tetto</strong>{' '}
              (concesso in uso a Revogreen Srl).
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form / Download */}
      <section className="py-14 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          {!submitted ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white border border-slate-200 rounded-2xl p-6 md:p-10 shadow-sm"
              data-testid="clausole-download-form-card"
            >
              <div className="flex items-start gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#FF5722]/10 flex items-center justify-center shrink-0">
                  <Download className="w-5 h-5 text-[#FF5722]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                    Richiedi il documento
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Compila i dati sottostanti per ricevere il PDF delle condizioni contrattuali.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="clausole-form">
                <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="text"
                      required
                      placeholder="Nome e Cognome *"
                      value={form.name}
                      onChange={(e) => update('name', e.target.value)}
                      className="pl-10 h-12 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-[#FF5722] rounded-xl"
                      data-testid="clausole-input-name"
                    />
                  </div>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="tel"
                      required
                      placeholder="Telefono *"
                      value={form.phone}
                      onChange={(e) => update('phone', e.target.value)}
                      className="pl-10 h-12 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-[#FF5722] rounded-xl"
                      data-testid="clausole-input-phone"
                    />
                  </div>
                </div>

                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    required
                    placeholder="Email *"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    className="pl-10 h-12 bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-[#FF5722] rounded-xl"
                    data-testid="clausole-input-email"
                  />
                </div>

                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                  <Select value={form.province} onValueChange={(v) => update('province', v)}>
                    <SelectTrigger className="pl-10 h-12 bg-white border-slate-200 text-slate-900 rounded-xl" data-testid="clausole-input-province">
                      <SelectValue placeholder="Provincia *" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {provinces.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Checkbox condizioni */}
                <label className="flex items-start gap-3 cursor-pointer group" data-testid="clausole-check-terms-label">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    required
                    className="mt-1 w-4 h-4 accent-[#FF5722] cursor-pointer shrink-0"
                    data-testid="clausole-check-terms"
                  />
                  <span className="text-sm text-slate-600 leading-relaxed">
                    Dichiaro di aver preso visione e di accettare integralmente le{' '}
                    <strong className="text-slate-900">clausole contrattuali</strong> di Evoluzione Tetto
                    (Revogreen Srl). *
                  </span>
                </label>

                {/* Checkbox privacy */}
                <label className="flex items-start gap-3 cursor-pointer group" data-testid="clausole-check-privacy-label">
                  <input
                    type="checkbox"
                    checked={acceptPrivacy}
                    onChange={(e) => setAcceptPrivacy(e.target.checked)}
                    required
                    className="mt-1 w-4 h-4 accent-[#FF5722] cursor-pointer shrink-0"
                    data-testid="clausole-check-privacy"
                  />
                  <span className="text-sm text-slate-600 leading-relaxed">
                    Ho letto e accetto la{' '}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#FF5722] hover:text-[#FF7043] underline">
                      Privacy Policy
                    </a>{' '}
                    ai sensi del Reg. UE 2016/679 (GDPR). *
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                  data-testid="clausole-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold px-6 py-4 rounded-xl transition-all mt-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Scarica le Clausole (PDF)
                    </>
                  )}
                </button>

                <p className="text-xs text-slate-400 text-center pt-1">
                  I tuoi dati verranno trattati ai soli fini della trasmissione del documento e per gli
                  eventuali contatti richiesti.
                </p>
              </form>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="bg-white border border-emerald-200 rounded-2xl p-8 md:p-12 text-center shadow-sm"
              data-testid="clausole-success"
            >
              <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                Grazie {form.name.split(' ')[0]}!
              </h2>
              <p className="text-slate-600 mb-2">
                Il download del PDF è iniziato automaticamente.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                Ti abbiamo inviato una copia del link a <strong className="text-slate-700">{form.email}</strong>.
                Il link scade tra <strong className="text-slate-700">24 ore</strong>.
              </p>
              <button
                onClick={() => triggerDownload(downloadUrl)}
                data-testid="clausole-redownload"
                className="inline-flex items-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] text-white font-bold px-6 py-3 rounded-full transition-all"
              >
                <Download className="w-4 h-4" />
                Scarica di nuovo
              </button>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />

      <LeadFormPopup isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </div>
  );
};

export default ClausolePage;
